// Порядок слоёв карты. Вынесено из MapCanvas ради проверяемости: сам порядок
// решает, видно ли стрелки и снос вообще.
//
// Растровый data-слой перерисовывается при каждой смене даты/глубины
// (removeLayer + addLayer). Без явного beforeId MapLibre кладёт его НАВЕРХ, и
// фон течений ложится поверх стрелок и маркеров сноса — на проде это выглядело
// как «стрелки бледные, точки блёклые»: их закрывал полупрозрачный растр.

import type { AddLayerObject } from 'maplibre-gl'
import { mapColorOf } from '../brand/mapColors'

/** Префикс всех оверлейных слоёв (стрелки, снос, косяки, тайл-оверлеи). */
export const OVERLAY_PREFIX = 'cabinet-overlay-'

/** Префикс слоёв изобат. Они тоже оверлей (растр ложится под них), но САМЫЙ
 *  НИЖНИЙ: линии дна — подложка под стрелками, точками и сносом, иначе рельеф
 *  перечёркивал бы то, ради чего карта открыта. */
export const ISOBATH_PREFIX = OVERLAY_PREFIX + 'isobath-'

/** id самого нижнего оверлея — под него вставляется растр.
 *  `undefined` (оверлеев нет) означает «класть сверху», как и раньше.
 *  Список — слои стиля в порядке отрисовки, снизу вверх. */
export function firstOverlayLayerId(layerIds: string[]): string | undefined {
  return layerIds.find(id => id.startsWith(OVERLAY_PREFIX))
}

/** Куда вставлять слой изобат: под самый нижний оверлей, КРОМЕ самих изобат.
 *
 *  Изобаты пропускаем намеренно: вставляя каждую следующую под уже лежащую, мы
 *  перевернули бы их порядок между собой (1000 м оказалась бы под 50 м).
 *  `undefined` — оверлеев нет, слой ложится сверху (над растром). */
export function isobathBeforeId(layerIds: string[]): string | undefined {
  return layerIds.find(id => id.startsWith(OVERLAY_PREFIX) && !id.startsWith(ISOBATH_PREFIX))
}

/** Префиксы РУБЕЖЕЙ: границы ИЭЗ, промысловые районы, запретные районы.
 *  Рубеж лежит ПОВЕРХ любой картинки — растра поля, льда, тумана, облачности,
 *  спутникового снимка и площадных заливок (решение владельца 18.09.2026):
 *  капитан читает по нему «где можно ловить», и рубеж, пропавший под льдом,
 *  читается как «рубежа тут нет». */
export const BOUNDARY_PREFIXES: readonly string[] = [
  OVERLAY_PREFIX + 'eez-',
  OVERLAY_PREFIX + 'fd-',
  OVERLAY_PREFIX + 'fb-',
  // Навигационные предупреждения NGA: зона стрельб под растром читалась бы
  // как «здесь не стреляют».
  OVERLAY_PREFIX + 'nw-',
]

const isBoundary = (id: string): boolean => BOUNDARY_PREFIXES.some(p => id.startsWith(p))

/** id самого нижнего слоя рубежей — под него ложится картинка.
 *  `undefined` — рубежей на карте нет, картинка кладётся сверху, как раньше;
 *  пришедшие позже рубежи лягут над ней сами. */
export function boundaryBeforeId(layerIds: string[]): string | undefined {
  return layerIds.find(isBoundary)
}

/** Минимум карты, нужный укладке, — ради карты-заглушки в тесте. */
interface StackMap {
  getStyle: () => { layers?: Array<{ id: string }> }
  addLayer: (spec: AddLayerObject, before?: string) => unknown
}

/** Положить картинку (растр, заливку) ПОД рубежи.
 *
 *  Одна дорога на все такие слои, а не `beforeId` у каждого вызова: слой,
 *  переукладываемый на смене даты (лёд, туман, облачность), без точки
 *  вставки ложится наверх, а повторная укладка рубежей зовёт только `setData`
 *  и назад их не поднимает — рубеж молча пропадал под льдом. */
export function addUnderBoundaries(map: StackMap, spec: AddLayerObject): void {
  const before = boundaryBeforeId((map.getStyle().layers ?? []).map(l => l.id))
  map.addLayer(spec, before)
}

/** Цвет точек землетрясений на карте. Отсюда его берёт и легенда сайдбара —
 *  подобранный на глаз кружок разъехался бы со слоем при первой правке. */
export function earthquakeColor(): string {
  return mapColorOf('earthquake')
}
